// pages/SubjectsPageAdmin.jsx
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  styled,
  alpha,
} from "@mui/material/styles";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  InputAdornment,
  Chip,
  Stack,
  MenuItem,
  Avatar,
  CircularProgress,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import {

  createSubject,
  deleteSubject,
  getSubjects,
  fetchExams,
  updateSubject,
} from "../utils/generalSubjectApi";


/* ── shared tokens ── */
const glass = {
  backdropFilter: "blur(24px) saturate(180%)",
  WebkitBackdropFilter: "blur(24px) saturate(180%)",
  background: "rgba(255,255,255,0.72)",
  border: "1px solid rgba(255,255,255,0.9)",
  boxShadow:
    "0 8px 32px rgba(15,23,42,0.07), inset 0 1px 0 rgba(255,255,255,0.95)",
};

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));
/* subject icon palette */
const SUBJECT_PALETTES = [
  {
    gradient: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    glow: "rgba(99,102,241,0.28)",
    light: "rgba(99,102,241,0.08)",
  },
  {
    gradient: "linear-gradient(135deg,#06b6d4,#0ea5e9)",
    glow: "rgba(6,182,212,0.28)",
    light: "rgba(6,182,212,0.08)",
  },
  {
    gradient: "linear-gradient(135deg,#10b981,#34d399)",
    glow: "rgba(16,185,129,0.28)",
    light: "rgba(16,185,129,0.08)",
  },
  {
    gradient: "linear-gradient(135deg,#f59e0b,#fbbf24)",
    glow: "rgba(245,158,11,0.28)",
    light: "rgba(245,158,11,0.08)",
  },
  {
    gradient: "linear-gradient(135deg,#ec4899,#f43f5e)",
    glow: "rgba(236,72,153,0.28)",
    light: "rgba(236,72,153,0.08)",
  },
  {
    gradient: "linear-gradient(135deg,#f97316,#fb923c)",
    glow: "rgba(249,115,22,0.28)",
    light: "rgba(249,115,22,0.08)",
  },
];
const getPalette = (id) => SUBJECT_PALETTES[id % SUBJECT_PALETTES.length];
const getInitials = (name) =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

/* ── SubjectCard ── */
const SubjectCard = ({ sub, index, onEdit, onDelete, navigate }) => {
  const palette = getPalette(sub.id);
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.94 }}
      transition={{
        duration: 0.45,
        delay: index * 0.04,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -6, transition: { duration: 0.22 } }}
      style={{ height: "100%" }}
    >
      <Card
        sx={{
          ...glass,
          height: "100%",
          borderRadius: "24px",
          overflow: "hidden",
          cursor: "pointer",
          position: "relative",
          transition: "box-shadow 0.3s",
          "&:hover": {
            boxShadow: `0 24px 56px ${palette.glow}, inset 0 1px 0 rgba(255,255,255,0.95)`,
          },
        }}
      >
        {/* top accent */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: palette.gradient,
          }}
        />

        <CardContent
          sx={{
            p: 3.5,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* icon + name */}
          <Stack direction="row" alignItems="center" spacing={2} mb={2.5}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: palette.gradient,
                boxShadow: `0 6px 16px ${palette.glow}`,
                fontSize: "0.95rem",
                fontWeight: 800,
                fontFamily: "'Clash Display', sans-serif",
                flexShrink: 0,
              }}
            >
              {getInitials(sub.name)}
            </Avatar>
            <Typography
              variant="h6"
              onClick={() => navigate(`/admin-subject/${sub.id}/videos`)}
              sx={{
                fontFamily: "'Clash Display', sans-serif",
                fontWeight: 700,
                fontSize: "1.05rem",
                color: "#0f172a",
                lineHeight: 1.3,
                textTransform: "capitalize",
                flex: 1,
                "&:hover": { color: "#6366f1" },
                transition: "color 0.2s",
              }}
            >
              {sub.name}
            </Typography>
            {sub.exam_name && (
              <Chip
                label={sub.exam_name}
                size="small"
                icon={<MenuBookRoundedIcon />}
                sx={{
                  mt: 0.5,
                  mb: 1,
                  width: "fit-content",
                  maxWidth: "100%",
                  borderRadius: "8px",
                  background: "rgba(99,102,241,0.09)",
                  color: "#6366f1",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  "& .MuiChip-icon": {
                    color: "#6366f1",
                  },
                }}
              />
            )}
          </Stack>

          {/* view link */}
          <Box
            onClick={() => navigate(`/admin-subject/${sub.id}/videos`)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              mt: "auto",
              mb: 2,
              px: 1.5,
              py: 0.8,
              borderRadius: "10px",
              background: palette.light,
              width: "fit-content",
              transition: "all 0.2s",
              "&:hover": { gap: 1.5 },
            }}
          >
            <VideoLibraryRoundedIcon
              sx={{
                fontSize: 15,
                color: palette.gradient.includes("6366f1")
                  ? "#6366f1"
                  : "#06b6d4",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "#475569",
                letterSpacing: "0.05em",
              }}
            >
              VIEW VIDEOS
            </Typography>
            <ArrowForwardRoundedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
          </Box>

          {/* actions */}
          <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onEdit(sub);
              }}
              size="small"
              sx={{
                color: "#6366f1",
                width: 38,
                height: 38,
                borderRadius: "12px",
                "&:hover": { background: "rgba(99,102,241,0.1)" },
              }}
            >
              <EditRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onDelete(sub.id);
              }}
              size="small"
              sx={{
                color: "#ef4444",
                width: 38,
                height: 38,
                borderRadius: "12px",
                "&:hover": { background: "rgba(239,68,68,0.08)" },
              }}
            >
              <DeleteRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </CardContent>
      </Card>
    </motion.div>
  );
};

/* ── main page ── */
export default function SubjectsPageAdmin() {
  const { countryId } = useParams();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [name, setName] = useState("");
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();


  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      setSubjects((await getSubjects(countryId)) || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [countryId]);

  useEffect(() => {
    const loadExams = async () => {
      try {
        const data = await fetchExams(countryId);
        setExams(data);
      } catch (error) {
        console.error(error);
        alert("Failed to fetch exams.");
      }
    };

    loadExams();
  }, [countryId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = subjects.filter((s) =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenForm = (sub = null) => {
    setEditingSubject(sub);
    setName(sub?.name || "");
    setSelectedExam(sub?.exam || "");
    setOpenForm(true);
  };
  const handleCloseForm = () => {
    setOpenForm(false);
    setEditingSubject(null);
    setName("");
    setSelectedExam("");
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;

    try {
      if (editingSubject) {
        setLoading(true);

        await updateSubject(editingSubject.id, {
          name: name.trim(),
          exam: selectedExam || null,
          country: countryId,
        });

        setSnackbar({
          open: true,
          message: "Subject updated ✓",
          severity: "success",
        });
      } else {
        await createSubject({
          name: name.trim(),
          exam: selectedExam || null,
          country: countryId,
        });

        setSnackbar({
          open: true,
          message: "Subject created ✓",
          severity: "success",
        });
      }

      handleCloseForm();
      fetchData();
    } catch (error) {
      console.error(error);

      setSnackbar({
        open: true,
        message: "Operation failed",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setLoading(true);
      await deleteSubject(confirmDeleteId);
      setSnackbar({ open: true, message: "Subject deleted", severity: "info" });
      setConfirmDeleteId(null);
      fetchData();
    } catch {
      setSnackbar({ open: true, message: "Delete failed", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(160deg,#f8faff 0%,#f1f5ff 50%,#faf8ff 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ambient orbs */}
      <Box
        sx={{
          position: "fixed",
          top: -160,
          left: -120,
          width: 520,
          height: 520,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(99,102,241,0.1) 0%,transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          top: 100,
          right: -80,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(16,185,129,0.08) 0%,transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(99,102,241,0.055) 1px,transparent 1px)",
          backgroundSize: "26px 26px",
          pointerEvents: "none",
          zIndex: 0,
          maskImage:
            "radial-gradient(ellipse 80% 80% at 50% 50%,black 50%,transparent 100%)",
        }}
      />
      <Box
        sx={{
          maxWidth: 1320,
          mx: "auto",
          px: { xs: 2.5, sm: 4, md: 6 },
          pt: { xs: 4, md: 7 },
          pb: 10,
          position: "relative",
          zIndex: 1,
        }}
      >
        <BackBtn
          startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
          onClick={() => navigate(-1)}
        >
          Back
        </BackBtn>
        {/* ── HEADER ── */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <Stack direction="row" alignItems="center" spacing={2} mb={1}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "16px",
                background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(99,102,241,0.38)",
                flexShrink: 0,
              }}
            >
              <MenuBookRoundedIcon sx={{ color: "#fff", fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Clash Display', sans-serif",
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.6rem" },
                  color: "#0f172a",
                  letterSpacing: "-0.04em",
                  lineHeight: 1.1,
                }}
              >
                Subjects
              </Typography>
              <Typography
                sx={{ color: "#64748b", fontSize: "0.9rem", fontWeight: 500 }}
              >
                Manage your learning catalogue
              </Typography>
            </Box>
          </Stack>
        </motion.div>

        {/* ── CONTROLS ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Box
            sx={{
              ...glass,
              borderRadius: "20px",
              mt: 5,
              p: { xs: 2, sm: 2.5 },
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              placeholder="Search subjects…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                maxWidth: { sm: 400 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  background: "rgba(255,255,255,0.6)",
                  "& fieldset": { borderColor: "rgba(0,0,0,0.09)" },
                  "&:hover fieldset": { borderColor: "rgba(99,102,241,0.4)" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#6366f1",
                    borderWidth: 1.5,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon
                      sx={{ color: "#94a3b8", fontSize: 20 }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            {!loading && (
              <Chip
                label={`${filtered.length} subject${
                  filtered.length !== 1 ? "s" : ""
                }`}
                sx={{
                  background: "rgba(99,102,241,0.1)",
                  color: "#6366f1",
                  fontWeight: 700,
                  border: "1px solid rgba(99,102,241,0.2)",
                  borderRadius: "10px",
                  height: 34,
                }}
              />
            )}
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => handleOpenForm()}
              sx={{
                ml: "auto",
                px: 4,
                py: 1.5,
                borderRadius: "14px",
                fontWeight: 700,
                fontSize: "0.9rem",
                background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
                boxShadow: "0 8px 24px rgba(99,102,241,0.38)",
                "&:hover": {
                  boxShadow: "0 14px 32px rgba(99,102,241,0.48)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.25s ease",
                minWidth: { xs: "100%", sm: "auto" },
              }}
            >
              New Subject
            </Button>
          </Box>
        </motion.div>

        {/* ── GRID ── */}
        <Box sx={{ mt: 5 }}>
          <AnimatePresence mode="wait">
            {loading ? (
              <Grid container spacing={3}>
                {[...Array(6)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Card sx={{ ...glass, borderRadius: "24px", height: 180 }}>
                      <CardContent sx={{ p: 3.5 }}>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          mb={3}
                        >
                          <Skeleton variant="circular" width={48} height={48} />
                          <Skeleton variant="text" width="60%" height={28} />
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="flex-end"
                          spacing={1}
                        >
                          <Skeleton variant="circular" width={38} height={38} />
                          <Skeleton variant="circular" width={38} height={38} />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : filtered.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ textAlign: "center", paddingTop: 100 }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "24px",
                    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                    boxShadow: "0 12px 32px rgba(99,102,241,0.35)",
                  }}
                >
                  <MenuBookRoundedIcon sx={{ color: "#fff", fontSize: 36 }} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: "'Clash Display', sans-serif",
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    color: "#0f172a",
                    mb: 1,
                  }}
                >
                  {searchTerm ? "No results found" : "No subjects yet"}
                </Typography>
                <Typography
                  sx={{ color: "#64748b", mb: 4, maxWidth: 340, mx: "auto" }}
                >
                  {searchTerm
                    ? `Nothing matches "${searchTerm}"`
                    : "Create your first subject to start building your content library"}
                </Typography>
                {!searchTerm && (
                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={() => handleOpenForm()}
                    sx={{
                      px: 5,
                      py: 1.5,
                      borderRadius: "14px",
                      fontWeight: 700,
                      background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
                    }}
                  >
                    Create Subject
                  </Button>
                )}
              </motion.div>
            ) : (
              <Grid container spacing={3}>
                {filtered.map((sub, i) => (
                  <Grid item xs={12} sm={6} md={4} key={sub.id}>
                    <SubjectCard
                      sub={sub}
                      index={i}
                      onEdit={handleOpenForm}
                      onDelete={setConfirmDeleteId}
                      navigate={navigate}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </AnimatePresence>
        </Box>
      </Box>

      {/* // ── CREATE/EDIT DIALOG ── */}
      <Dialog
        open={openForm}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...glass,
            borderRadius: "28px",
            boxShadow: "0 40px 80px rgba(0,0,0,0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Clash Display', sans-serif",
            fontWeight: 700,
            fontSize: "1.4rem",
            pt: 4,
            px: 4,
            pb: 0.5,
            color: "#0f172a",
          }}
        >
          {editingSubject ? "Edit Subject" : "New Subject"}
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 2, pt: 1 }}>
          <Typography sx={{ color: "#64748b", fontSize: "0.85rem", mb: 2.5 }}>
            {editingSubject
              ? "Update the subject name below."
              : "Give your subject a clear, descriptive name."}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Subject name"
            placeholder="e.g. Advanced Mathematics"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                fontSize: "1rem",
              },
            }}
          />
          <TextField
            select
            fullWidth
            label="Exam"
            value={selectedExam}
            onChange={(e) => setSelectedExam(e.target.value)}
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                fontSize: "1rem",
              },
            }}
          >
            <MenuItem value="">
              <em>Select exam</em>
            </MenuItem>

            {exams.map((exam) => (
              <MenuItem key={exam.id} value={exam.id}>
                {exam.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, pt: 2, gap: 1 }}>
          <Button
            onClick={handleCloseForm}
            sx={{
              color: "#64748b",
              fontWeight: 600,
              borderRadius: "12px",
              px: 3,
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setLoading(true);
              await handleSubmit();
              setLoading(false);
            }}
            disabled={!name.trim() || loading}
            sx={{
              px: 5,
              py: 1.4,
              borderRadius: "14px",
              fontWeight: 700,
              background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
              boxShadow: "0 6px 20px rgba(99,102,241,0.38)",
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "white" }} />
            ) : editingSubject ? (
              "Save Changes"
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      
      {/* ── DELETE CONFIRM ── */}
      <Dialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", ...glass } }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Clash Display', sans-serif",
            fontWeight: 700,
            color: "#ef4444",
            px: 4,
            pt: 4,
          }}
        >
          Delete Subject?
        </DialogTitle>
        <DialogContent sx={{ px: 4 }}>
          <Typography sx={{ color: "#64748b", mt: 1, lineHeight: 1.7 }}>
            This action cannot be undone. All associated videos will be
            affected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, gap: 1 }}>
          <Button
            onClick={() => setConfirmDeleteId(null)}
            sx={{ color: "#64748b", fontWeight: 600 }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              setLoading(true);
              await handleDelete();
              setLoading(false);
            }}
            sx={{ borderRadius: "12px", fontWeight: 700, px: 4 }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "white" }} />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── SNACKBAR ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3200}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          sx={{
            borderRadius: "14px",
            fontWeight: 600,
            boxShadow: "0 12px 32px rgba(0,0,0,0.14)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
